import dayjs from 'dayjs';

import {
  curry, compose,
  mergeAll, path, map, join,
  lensPath, set, over, pathOr,
  values, isEmpty, isNil,
  reject
} from 'ramda';
import Utils from 'src/utils';
import { I18n } from 'src/translations/i18n';

const defDayPath = ['Period', 'StartDate'];

const parseDepartment = (confirmLevels) => (department) => {
  const {
    Id,
    Name,
    TotalHours,
    TotalsByPeriod = [],
    Users = []
  } = department;

  return {
    id: Id,
    name: Name,
    total: {
      total: TotalHours,
      byDays: periodicToByDays(
        ['Period', 'StartDate'],
        ['TotalHours'],
        null,
        TotalsByPeriod
      )
    },
    jobsByUsers: map(parseUser(confirmLevels), Users)
  };
};

const parseUser = (confirmLevels) => (user) => {
  const {
    Id,
    Name,
    ExternalCode,
    DataByPeriods = [],
    TotalsByJob,
    TotalWork,
    Confirmations = [],
    ...other
  } = user;

  const overUnderByDays = periodicToByDays(defDayPath, ['WorkTotal', 'PlannedWorkShifts'], parseWorkShifts, DataByPeriods);
  const plannedByDays = periodicToByDays(defDayPath, ['WorkTotal', 'PlannedHours'], null, DataByPeriods);
  const hoursWorkedByDays = periodicToByDays(defDayPath, ['WorkTotal', 'WorkHours'], null, DataByPeriods);
  const differenceByDays = periodicToByDays(defDayPath, ['WorkTotal', 'TimeDifferenceHours'], null, DataByPeriods);
  const differenceHoursByDays = periodicToByDays(defDayPath, ['WorkTotal', 'TimeDifference'], null, DataByPeriods);
  const absencesByDays = periodicToByDays(defDayPath, ['WorkTotal', 'Leave'], null, DataByPeriods);
  const errors = periodicToByDays(defDayPath, ['WorkTotal', 'Errors'], null, DataByPeriods);
  const totalErrors = TotalWork.Errors;

  const overUnderTotal = path(['OverUnderHours'], user);
  const plannedTotal = path(['TotalWork', 'PlannedHours'], user);
  const hoursWorkedTotal = path(['TotalWork', 'WorkHours'], user);
  const differenceTotal = path(['TotalWork', 'TimeDifferenceHours'], user);
  const absencesTotal = path(['AbsenceHours'], user);

  const actions = map(collectActionData(DataByPeriods), (TotalsByJob || []));

  const compensateWithFreeTimeIdx = actions.findIndex(({ name }) => name === I18n.t('CompensateWithFreeTime'));
  if (compensateWithFreeTimeIdx !== -1) {
    const lastProfessionIndex = actions.reduce((acc, { profession }, index) => {
      if (profession) acc = index;
      return acc;
    }, -1);
    if (lastProfessionIndex !== -1) {
      const compensateWithFreeTime = actions[compensateWithFreeTimeIdx];
      actions.splice(compensateWithFreeTimeIdx, 1);
      actions.splice(lastProfessionIndex + 1, 0, compensateWithFreeTime);
    }
  }

  const confirmations = compose(
    // normalizeList("level"),
    buildConfirmations(confirmLevels),
    map(parseConfirmation),
    map(Utils.objects.decapitalizeObject)
  )(Confirmations || []);

  return {
    user: {
      id: Id,
      name: Name,
      externalCode: ExternalCode
    },
    overUnderTime: {
      total: overUnderTotal,
      byDays: overUnderByDays
    },
    planned: {
      total: plannedTotal,
      byDays: plannedByDays
    },
    hoursWorked: {
      total: hoursWorkedTotal,
      byDays: hoursWorkedByDays
    },
    difference: {
      total: differenceTotal,
      byDays: differenceByDays,
      hoursByDays: differenceHoursByDays
    },
    absences: {
      total: absencesTotal,
      byDays: absencesByDays
    },
    totalErrors,
    errors,
    actions,
    confirmations,

    ...other
  };
};

// #12347 - workshift codes in main report: cut first characters until first space.
const parseWorkShifts = compose(
  join(', '),
  map((workshift) => {
    const [first] = workshift.split(' ', 1);
    return first;
  }),
  (value) => value || []
);

const buildConfirmations = (confirmLevels) => (conf) => {
  const confirmations = Array.from({ length: confirmLevels });

  conf.forEach((confirmation) => {
    const { level } = confirmation;
    if (level <= confirmLevels) {
      confirmations[level - 1] = confirmation;
    }
  });

  return confirmations;
};

const parseConfirmation = ({
  dateTime,
  level,
  name,
  ...other
}) => ({
  ...other,
  // isConfirmed: !isEmpty(name) && !isEmpty(dateTime),
  title: name,
  date: !isEmpty(dateTime)
    ? dayjs(dateTime).format('DD.MM.YYYY HH:mm')
    : null,
  level
});

const isOvertimeTransferJob = (job) => {
  const { Name, ExternalCode, Id } = job;
  return Name === 'OverTime Transfer' && ExternalCode === null && Id === 0;
};

const collectActionData = curry((DataByPeriods, job) => {
  const {
    Id: jobId,
    ExternalCode: externalCode,
    Name,
    TotalHours,
    WorkTimeByType,
    ProfessionName
  } = job;

  const byType = (WorkTimeByType || []).reduce((acc, {
    Id: typeId,
    Name: type,
    Hours: total,
    ExternalCode
  }) => {
    acc[typeId] = {
      total,
      type,
      byDays: {},
      externalCode: ExternalCode
    };

    return acc;
  }, {});

  const name = isOvertimeTransferJob(job)
    ? I18n.t('CompensateWithFreeTime')
    : Name;
  const data = {
    total: TotalHours,
    name,
    externalCode,
    byType,
    byDays: {},
    profession: ProfessionName
  };

  const actions = DataByPeriods.reduce((acc, periodic) => {
    const day = dayjs(path(['Period', 'StartDate'], periodic)).format('YYYY-MM-DD');
    const dayActions = pathOr([], ['JobsByType'], periodic);
    const action = dayActions.find(({ Id }) => Id === jobId);

    if (action) {
      acc.byDays[day] = pathOr('', ['TotalHours'], action);
      const { WorkTimeByType: workTimeByType } = action;
      (workTimeByType || []).forEach((type) => {
        const { Id, Hours } = type;
        const byTypeDayL = lensPath(['byType', Id, 'byDays', day]);
        acc = set(byTypeDayL, Hours, acc);
      });
    }

    return acc;
  }, data);

  const byTypeL = lensPath(['byType'], actions);
  return over(byTypeL, values, actions);
});

const periodicToByDays = curry((
  dayPath,
  valuePath,
  transformer,
  data
) => compose(
  reject(isNil),
  mergeAll,
  map((periodic) => {
    const day = dayjs(path(dayPath, periodic)).format('YYYY-MM-DD');
    const value = path(valuePath, periodic);

    const transformed = transformer ? transformer(value) : value;
    return { [day]: transformed };
  })
)(data));

const parseMainReport = (confirmLevels, departments) => map(parseDepartment(confirmLevels), departments);

export default parseMainReport;
